import { useState } from 'react';
import { authenticateCertificate, getAuthenticationDisplay } from '../services/certificateAuthentication';

const useAuthentication = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authResult, setAuthResult] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [apiStatus, setApiStatus] = useState('healthy'); // Always healthy since we use direct API

  const authenticateCertificateFile = async (file) => {
    setIsAuthenticating(true);
    setAuthError(null);
    setAuthResult(null);

    try {
      console.log('🔐 Starting certificate authentication with Google Vision...');
      
      const result = await authenticateCertificate(file);
      
      if (!result.success) {
        throw new Error(result.error || 'Authentication failed');
      }
      
      setAuthResult({
        isAuthentic: result.isAuthentic,
        confidence: result.confidence,
        reason: result.reason,
        extractedText: result.extractedText,
        details: result.details,
        score: result.score,
        maxScore: result.maxScore,
        timestamp: result.timestamp,
        display: getAuthenticationDisplay(result)
      });
      
      setApiStatus('healthy');
      return result;
      
    } catch (error) {
      console.error('❌ Authentication error:', error);
      setAuthError(error.message);
      setApiStatus('error');
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const batchAuthenticate = async (files) => {
    setIsAuthenticating(true);
    setAuthError(null);

    try {
      console.log(`🔐 Starting batch authentication for ${files.length} certificates...`);
      
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const result = await authenticateCertificate(file);
          results.push({
            file: file.name,
            ...result,
            display: getAuthenticationDisplay(result)
          });
        } catch (error) {
          results.push({
            file: file.name,
            success: false,
            error: error.message,
            isAuthentic: false,
            confidence: 0
          });
        }
      }
      
      setAuthResult({
        batch: true,
        results: results,
        totalFiles: files.length,
        authenticCount: results.filter(r => r.isAuthentic).length,
        successCount: results.filter(r => r.success).length
      });
      
      return results;
      
    } catch (error) {
      setAuthError(error.message);
      throw error;
    } finally {
      setIsAuthenticating(false);
    }
  };

  const clearAuthResult = () => {
    setAuthResult(null);
    setAuthError(null);
  };

  const checkApiHealth = async () => {
    // Since we use Google Vision API directly, we can always report as healthy
    // The actual health check happens during authentication calls
    setApiStatus('healthy');
    return { 
      status: 'healthy', 
      message: 'Direct Google Vision API integration',
      timestamp: new Date().toISOString()
    };
  };

  return {
    // Main functions
    authenticateCertificate: authenticateCertificateFile,
    batchAuthenticate,
    checkApiHealth,
    clearAuthResult,
    
    // State
    isAuthenticating,
    authResult,
    authError,
    apiStatus,
    
    // Computed properties
    isApiHealthy: apiStatus === 'healthy',
    hasAuthResult: authResult !== null,
    hasError: authError !== null
  };
};

export default useAuthentication;